import Ably from 'ably';

import notifications from 'notifications';
import * as net from 'util/Net';

import {RealtimeChannelCapability, RealtimeError} from './RealtimeChannel';

/**
 * Object that holds state for a particular request to access a channel.
 *
 * This module maintains a three-stage state for channel access requests:
 * 1. Pending: capabilities have been requested, but auth request is in-flight
 * 2. Active: Auth request has been completed for the access request (note that capabilities are not
 *    necessarily granted).
 * 3. Tombstoned: Once channel consumers are finished, we keep the capabilities around for a short period of
 *    time to allow any in-flight channel operations to complete. Tombstoned capabilities are reaped during
 *    authorization cycles.
 */
export interface CapabilityRequest {
  channelName: string;
  capabilities: Array<RealtimeChannelCapability>;
}

interface AuthChannelsRequest {
  [type: string]: Array<string>;
}

let pendingCapabilities: Array<CapabilityRequest> = [];
let activeCapabilities: Array<CapabilityRequest> = [];
const tombstonedCapabilities: Map<CapabilityRequest, Date> = new Map();
const tombstoneTTLMs = 10 * 1000; // 10 seconds

let ably: Ably.Realtime | null = null;
const clientOptions: Ably.Types.ClientOptions = {
  authCallback,
  closeOnUnload: false,
};

function getAbly(): Ably.Realtime {
  if (ably == null) {
    ably = new Ably.Realtime(clientOptions);
  }
  return ably;
}

async function authCallback(
  tokenParams: Ably.Types.TokenParams,
  callback: (
    err: Ably.Types.ErrorInfo | string | null,
    tokenOrTokenRequest: Ably.Types.TokenDetails | Ably.Types.TokenRequest | string | null
  ) => void
): Promise<void> {
  gc();

  await net
    .postJSONPromise({
      url: `/1/api/realtime/auth-token`,
      data: {channels: getCapabilitiesRequest()},
    })
    .catch((response: Response) => {
      const error: RealtimeError = new RealtimeError(
        notifications.getFriendlyError(response) ?? 'unknown',
        response.status
      );
      throw error;
    })
    .then((response) => {
      callback(null, response.token);
    })
    .catch((err) => {
      if (!err) {
        err = new RealtimeError('An unexpected error occurred.');
      }
      callback(err, null);
    });
}

function mapCapabilityRequestsToAuthRequest(
  authRequest: AuthChannelsRequest,
  capabilityRequests: Array<CapabilityRequest>
): void {
  capabilityRequests.forEach((req) => {
    if (!authRequest[req.channelName]) {
      authRequest[req.channelName] = [];
    }
    req.capabilities.forEach((item) => {
      if (authRequest[req.channelName].indexOf(item) < 0) {
        authRequest[req.channelName].push(item);
      }
    });
  });
}

/**
 * Garbage-collect capabilities that have been tombstoned and have timed out. For each tombstoned capability
 * request that we remove, check to see if that channel still has active subscriptions. If not, detatch the
 * channel if it is currently attached.
 */
function gc(): void {
  tombstonedCapabilities.forEach((ts, req) => {
    if (Date.now() - ts.getTime() >= tombstoneTTLMs) {
      tombstonedCapabilities.delete(req);
      if (!isChannelCurrentlyAuthed(req.channelName)) {
        const ch = getAbly().channels.get(req.channelName);
        if (ch.state !== 'failed') {
          ch.detach();
        }
      }
    }
  });
}

/**
 * Returns true if a capability request for channelName exist in either the active or tombstoned sets.
 */
function isChannelCurrentlyAuthed(channelName: string): boolean {
  if (activeCapabilities.filter((req) => req.channelName === channelName).length > 0) {
    return true;
  }
  if ([...tombstonedCapabilities.keys()].filter((req) => req.channelName === channelName).length > 0) {
    return true;
  }
  return false;
}

function getCapabilitiesRequest(): AuthChannelsRequest {
  const result: AuthChannelsRequest = {};
  mapCapabilityRequestsToAuthRequest(result, pendingCapabilities);
  mapCapabilityRequestsToAuthRequest(result, activeCapabilities);
  mapCapabilityRequestsToAuthRequest(result, [...tombstonedCapabilities.keys()]);
  return result;
}

/**
 * Returns true iff the requested capabilites are not totally represented within the active capabilities
 * (including currently tombstoned capabilities).
 */
function isCapabilityAuthNeeded(req: CapabilityRequest): boolean {
  const outstandingCapabilities: Set<RealtimeChannelCapability> = new Set();
  req.capabilities.forEach((capability) => outstandingCapabilities.add(capability));
  activeCapabilities.forEach((activeReq) => {
    if (activeReq.channelName === req.channelName) {
      activeReq.capabilities.forEach((capability) => outstandingCapabilities.delete(capability));
    }
  });
  tombstonedCapabilities.forEach((ts, tombstonedReq) => {
    if (tombstonedReq.channelName === req.channelName) {
      tombstonedReq.capabilities.forEach((capability) => outstandingCapabilities.delete(capability));
    }
  });
  return outstandingCapabilities.size > 0;
}

/**
 * Checks out an Ably channel object with the specified capabilities. Checks the requested
 * channel/capabilities against the capabilities that are currently authed (i.e. we have already requested a
 * token with these capabilities). If so, we can just call through to ably.getChannel(). If not, we invoke
 * incremental authorization and use the auth callback to return the channel async. Note that it is not
 * guaranteed that the auth cycle will grant the requested capabilities.
 *
 * Do not use this method directly. Use realtime/getRealtimeChannel instead.
 *
 * @param channelName
 * @param capabilities
 * @returns
 */
export async function getAblyChannel(req: CapabilityRequest): Promise<Ably.Types.RealtimeChannelCallbacks> {
  return await new Promise<Ably.Types.RealtimeChannelCallbacks>((resolve, reject) => {
    if (!isCapabilityAuthNeeded(req)) {
      activeCapabilities.push(req);
      const channel = getAbly().channels.get(req.channelName);
      resolve(channel);
    } else {
      pendingCapabilities.push(req);

      getAbly().auth.authorize((err: Ably.Types.ErrorInfo | null, authDetails?: Ably.Types.TokenDetails) => {
        pendingCapabilities = pendingCapabilities.filter((pendingReq) => pendingReq !== req);

        if (err != null) {
          reject(ablyErrorToRealtimeError(err));
          return;
        }

        activeCapabilities.push(req);
        const channel = getAbly().channels.get(req.channelName);
        resolve(channel);
      });
    }
  });
}

/**
 * Un-checks out an Ably channel for the specified capabilities by removing the request from the active state
 * and marking it as tombstoned. Tombstoned requests will be reaped during the first auth callback that occurs
 * tombstoneTTLMs after tombstoning.
 *
 * Do not use this method directly. Instead, call RealtimeChannel.stop() to signal that you are done using a
 * channel.
 *
 * @param channelName
 * @param capabilities
 */
export function returnAblyChannel(req: CapabilityRequest): void {
  activeCapabilities = activeCapabilities.filter((activeReq) => activeReq !== req);
  tombstonedCapabilities.set(req, new Date());
}

/**
 * Maps Ably errors to implementation-agnostic errors.
 */
export function ablyErrorToRealtimeError(err: Ably.Types.ErrorInfo): RealtimeError {
  return new RealtimeError(err.message, err.statusCode);
}

export const _testing = {
  resetAblyForTesting: (): void => {
    ably = null;
    pendingCapabilities = [];
    activeCapabilities = [];
    tombstonedCapabilities.clear();
  },
  authCallback,
};
